/**
 * The website's implementation of the platform contract.
 *
 * Everything here is a plain browser API. Nothing imports a native plugin, so
 * this module is what the production web bundle carries and the only place
 * the website is allowed to reach for these capabilities.
 */
import type {
  AppVersion,
  GeoError,
  GeoErrorCode,
  GeoOptions,
  GeolocationCoords,
  KeyValueStore,
  Platform,
  SharePayload,
  Unsubscribe,
} from './types';

const noop: Unsubscribe = () => {};

function localStorageStore(prefix: string): KeyValueStore {
  const own = (k: string) => k.startsWith(prefix);
  return {
    async get(key) {
      try {
        return localStorage.getItem(prefix + key);
      } catch {
        return null;
      }
    },
    async set(key, value) {
      localStorage.setItem(prefix + key, value);
    },
    async remove(key) {
      localStorage.removeItem(prefix + key);
    },
    async keys() {
      const out: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && own(k)) out.push(k.slice(prefix.length));
      }
      return out;
    },
    async clear() {
      for (const k of await this.keys()) localStorage.removeItem(prefix + k);
    },
  };
}

// ── files: durable blobs in IndexedDB ────────────────────────────────────────
// The outbox (M10) needs payloads that survive a reload, so an in-memory Map
// would be a silent lie. One object store keyed by handle.
const DB_NAME = 'kanfy-platform-files';
const STORE = 'blobs';

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB is not available in this browser'));
      return;
    }
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) req.result.createObjectStore(STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB open failed'));
  });
}

function tx<T>(mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const t = db.transaction(STORE, mode);
        const req = run(t.objectStore(STORE));
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error ?? new Error('IndexedDB request failed'));
        t.oncomplete = () => db.close();
      }),
  );
}

function newHandle(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

// ── geolocation ───────────────────────────────────────────────────────────────
// Rejects with a GeoError. The browser's GeolocationPositionError carries the
// same codes, but its message is the browser's own wording ("User denied
// Geolocation", or a network provider's HTTP status) and useGeolocation shows
// `message` to the user — so the code is kept and the wording is ours.
const GEO_MESSAGES: Record<GeoErrorCode, string> = {
  1: 'Location permission was denied.',
  2: 'Your location is currently unavailable.',
  3: 'Timed out while getting your location.',
};

function getCurrentPosition(opts: GeoOptions): Promise<GeolocationCoords> {
  return new Promise((resolve, reject) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      const unsupported: GeoError = { code: 2, message: 'Geolocation is not supported on this device.' };
      reject(unsupported);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        resolve({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
          // The browser API has no coarse flag; useGeolocation's own
          // threshold (accuracy in metres) is the caller's to apply.
          coarse: false,
          timestamp: pos.timestamp,
        }),
      (err) => {
        const code: GeoErrorCode = err.code === 1 || err.code === 3 ? err.code : 2;
        const failure: GeoError = { code, message: GEO_MESSAGES[code] };
        reject(failure);
      },
      {
        enableHighAccuracy: opts.highAccuracy ?? false,
        timeout: opts.timeoutMs,
        maximumAge: opts.maximumAgeMs ?? 0,
      },
    );
  });
}

// ── links ─────────────────────────────────────────────────────────────────────
// A synthetic anchor click rather than window.open: the native bundle is
// gated against window.open (scripts/assert-no-window-open.mjs), and using
// the anchor here keeps this module clean under that scan even though it
// never ships natively.
function openInNewTab(url: string): void {
  const a = document.createElement('a');
  a.href = url;
  a.target = '_blank';
  a.rel = 'noopener noreferrer';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

const APP_SCHEMES = /^(tel|sms|mailto|geo):/i;

// ── app ───────────────────────────────────────────────────────────────────────
function onVisibility(cb: (active: boolean) => void): Unsubscribe {
  const handler = () => cb(document.visibilityState === 'visible');
  document.addEventListener('visibilitychange', handler);
  return () => document.removeEventListener('visibilitychange', handler);
}

const webVersion: AppVersion = {
  name: import.meta.env.VITE_BUILD_SHA ?? 'dev',
  // 0 on purpose: the minimum-version gate only ever compares native builds,
  // and a floor of 0 (the migration's default) means "no floor".
  build: 0,
};

export const webPlatform: Platform = {
  kind: 'web',

  push: {
    // §H.1: the website registers no service worker today, so there is
    // nothing to subscribe. Honest false rather than a fake success.
    async register() {
      return false;
    },
    async unregister() {},
    onOpen: () => noop,
    onMessage: () => noop,
    // Nothing is ever asked on the web while register() is a no-op, so the
    // honest answer is that push is off — never 'prompt', which would invite
    // a primer for a registration that cannot happen.
    async permission() {
      return 'denied';
    },
    onToken: () => noop,
  },

  camera: {
    // The website keeps its <input type="file" capture> flows; a generic
    // picker here would duplicate five existing upload paths.
    async pick() {
      return null;
    },
  },

  geolocation: { getCurrentPosition },

  share: {
    async share(payload: SharePayload) {
      const nav = navigator as Navigator & { share?: (d: ShareData) => Promise<void> };
      // NotSupportedError, like the browser's own: callers may fall back to a
      // download for it (saveOrShareFile), never for a real failure.
      if (typeof nav.share !== 'function') throw new DOMException('Web Share is not available in this browser', 'NotSupportedError');
      const data: ShareData = { title: payload.title, text: payload.text, url: payload.url };
      if (payload.file) data.files = [new File([payload.file], payload.file.name, { type: payload.file.type })];
      await nav.share(data);
    },
    // navigator.share takes the Blob itself; nothing is written to disk.
    async clearFiles() {},
  },

  links: {
    onAppUrlOpen: () => noop,
    async openExternal(url) {
      if (!/^https:\/\//i.test(url)) throw new Error('openExternal accepts https URLs only');
      openInNewTab(url);
    },
    async canOpenAppUrl(url) {
      return APP_SCHEMES.test(url);
    },
    async openAppUrl(url) {
      if (!APP_SCHEMES.test(url)) return false;
      window.location.assign(url);
      return true;
    },
  },

  storage: {
    // No Keystore on the web; "secure" is the same origin-scoped store the
    // Supabase client already trusts for its session (client.ts).
    secure: localStorageStore('kanfy.secure.'),
    prefs: localStorageStore('kanfy.prefs.'),
    async flush() {},
  },

  biometric: null,

  // No Play on the web: nothing to attest, and nothing to hash for it.
  integrity: null,

  app: {
    onStateChange: onVisibility,
    // Browser back is history, not a button; the shell reads history instead.
    onBackButton: () => noop,
    exit() {},
    async version() {
      return webVersion;
    },
  },

  network: {
    onChange(cb) {
      const on = () => cb(true);
      const off = () => cb(false);
      window.addEventListener('online', on);
      window.addEventListener('offline', off);
      return () => {
        window.removeEventListener('online', on);
        window.removeEventListener('offline', off);
      };
    },
    async isOnline() {
      return typeof navigator === 'undefined' ? true : navigator.onLine;
    },
  },

  ui: {
    haptic(kind) {
      const nav = navigator as Navigator & { vibrate?: (p: number | number[]) => boolean };
      if (typeof nav.vibrate !== 'function') return;
      nav.vibrate(kind === 'light' ? 10 : kind === 'success' ? [10, 30, 10] : [30, 30, 30]);
    },
    setStatusBar() {},
    keyboard: {
      onShow: () => noop,
      onHide: () => noop,
    },
    async secureScreen() {},
  },

  files: {
    async put(bytes) {
      const handle = newHandle();
      await tx('readwrite', (s) => s.put(bytes, handle));
      return handle;
    },
    async open(handle) {
      const v = await tx<Blob | undefined>('readonly', (s) => s.get(handle) as IDBRequest<Blob | undefined>);
      return v ?? null;
    },
    async remove(handle) {
      await tx('readwrite', (s) => s.delete(handle));
    },
    async usedBytes() {
      const all = await tx<Blob[]>('readonly', (s) => s.getAll() as IDBRequest<Blob[]>);
      return all.reduce((n, b) => n + (b?.size ?? 0), 0);
    },
    async list() {
      const keys = await tx<IDBValidKey[]>('readonly', (s) => s.getAllKeys());
      return keys.filter((k): k is string => typeof k === 'string');
    },
  },

  auth: {
    async googleIdToken() {
      return null;
    },
  },

  authRedirectOrigin: () => window.location.origin,
};
